/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { Buffer } from 'buffer';
import { gunzipSync } from 'zlib';

import qs from 'qs';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { getCurrentRepository } from 'src/selectors/repository-selectors';
import { authRequest } from 'src/utils/fetch';

import {
  EMPTY_STEP,
  RUNNER_UPLOAD_RAW_STEP_METRICS_ENABLED,
} from '../../constants';
import { Step } from '../../models';
import { getFeatureFlagFromStep } from '../../utils';
import envBaseUrl from '../../utils/env-base-url';
import {
  capturePipelinesExceptionWithTags,
  createErrorMessage,
} from '../../utils/sentry';
import { METRIC_UPDATED, REQUEST_METRICS } from '../actions/pipelines';

export const getMetricsUrl = (
  repositoryFullSlug: string,
  pipelineUuid: string,
  stepUuid: string,
  queryParams: {} = {}
) => {
  const baseUrl = `${envBaseUrl(
    '/!api/internal'
  )}/repositories/${repositoryFullSlug}/pipelines/${pipelineUuid}/steps/${stepUuid}/metrics`;

  if (Object.keys(queryParams).length === 0) {
    return `${baseUrl}/content`;
  }

  const allQueryParams = {
    pagelen: 100,
    ...queryParams,
  };

  return `${baseUrl}?${qs.stringify(allQueryParams, { allowDots: true })}`;
};

export function processMetrics(
  data: ArrayBuffer,
  isDataUncompressed: boolean = false
): any {
  try {
    const buffer = Buffer.from(data);

    if (isDataUncompressed) {
      const decodedData = new TextDecoder().decode(buffer);
      const jsonData = JSON.parse(decodedData);
      return jsonData.values;
    } else {
      const decompressedData = gunzipSync(buffer);
      const decodedDecompressedData = new TextDecoder().decode(
        decompressedData
      );

      const jsonMatch = decodedDecompressedData.match(/{.*}/s);
      if (!jsonMatch) {
        throw new Error('Failed to extract JSON from decompressed data');
      }

      const jsonData = JSON.parse(jsonMatch[0]);
      return jsonData.values;
    }
  } catch (error) {
    throw new Error(`Failed to process metrics data: ${error.message}`);
  }
}

export function* requestMetricsSaga(action: {
  meta: { pipelineUuid: string; step: Step };
  type: string;
}): Generator<any, any, any> {
  const meta = action.meta || action; // websocket is not grouping params in action
  if (!meta.pipelineUuid || !meta.step || meta.step.uuid === EMPTY_STEP) {
    return;
  }
  const { pipelineUuid, step } = meta;
  const stepUuid = step.uuid;
  const { full_name } = yield select(getCurrentRepository);

  const isRunnerUploadRawStepMetricsEnabled = getFeatureFlagFromStep(
    step.featureFlags,
    RUNNER_UPLOAD_RAW_STEP_METRICS_ENABLED
  );

  try {
    const res = yield call(
      fetch,
      authRequest(getMetricsUrl(full_name, pipelineUuid, stepUuid))
    );

    if (!res.ok) {
      throw new Error(yield createErrorMessage(res));
    }

    const arrayBuffer = yield call([res, 'arrayBuffer']);
    const payload = processMetrics(
      arrayBuffer,
      isRunnerUploadRawStepMetricsEnabled
    );

    yield put({
      type: REQUEST_METRICS.SUCCESS,
      meta: action.meta,
      payload,
    });
  } catch (e) {
    capturePipelinesExceptionWithTags(e, {
      segment: REQUEST_METRICS.ERROR,
    });
    yield put({
      type: REQUEST_METRICS.ERROR,
      meta: action.meta,
      payload: e,
    });
  }
}

// eslint-disable-next-line import/no-default-export
export default function* () {
  yield all([
    takeLatest(REQUEST_METRICS.REQUEST, requestMetricsSaga),
    takeLatest(METRIC_UPDATED, requestMetricsSaga),
  ]);
}
